import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { API_CONFIG } from '@/config/api.config'

/**
 * Página de callback OAuth
 * Recebe código descartável da URL e troca por JWT via API
 */
const AuthCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  useEffect(() => {
    const error = searchParams.get('error')
    const code = searchParams.get('code')

    // Compatibilidade: aceita token direto na URL (fallback temporário)
    const legacyToken = searchParams.get('token')

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro na autenticação',
        description: decodeURIComponent(error),
      })
      navigate('/auth')
      return
    }

    if (legacyToken) {
      // Fallback: se backend antigo ainda mandar token na URL
      localStorage.setItem('token', legacyToken)
      fetchUserAndRedirect(legacyToken)
      return
    }

    if (!code) {
      toast({
        variant: 'destructive',
        title: 'Código não fornecido',
        description: 'Erro no processo de autenticação',
      })
      navigate('/auth')
      return
    }

    // Troca código descartável por JWT
    fetch(`${API_CONFIG.baseURL}/auth/exchange-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Código inválido ou expirado')
        return res.json()
      })
      .then((data) => {
        localStorage.setItem('token', data.token)
        localStorage.setItem('user', JSON.stringify(data.user))
        toast({
          title: 'Login realizado com sucesso!',
          description: `Bem-vindo, ${data.user.name}!`,
        })
        navigate('/home')
      })
      .catch(() => {
        toast({
          variant: 'destructive',
          title: 'Erro na autenticação',
          description: 'Código expirado ou inválido. Tente novamente.',
        })
        navigate('/auth')
      })
  }, [searchParams, navigate, toast])

  function fetchUserAndRedirect(token: string) {
    fetch(`${API_CONFIG.baseURL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Não autenticado')
        return res.json()
      })
      .then((user) => {
        localStorage.setItem('user', JSON.stringify(user))
        toast({
          title: 'Login realizado com sucesso!',
          description: `Bem-vindo, ${user.name}!`,
        })
        navigate('/home')
      })
      .catch(() => {
        localStorage.removeItem('token')
        toast({
          variant: 'destructive',
          title: 'Erro ao buscar dados do usuário',
        })
        navigate('/auth')
      })
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Autenticando...</p>
      </div>
    </div>
  )
}

export default AuthCallback
